import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router';
import { Button, Result } from 'antd';
import { useStore } from '../../helpers/use-store';
import {
  ERROR_MESSAGES,
  ERROR_TYPES,
  UIErrorService,
} from '../../services/core/error-service';

import './error-page.scss';

/**
 * ErrorPage component renders a user-friendly error page with a message
 * and a button to navigate back to the home page. It utilizes the `useRouteError`
 * hook to capture any routing errors and logs them using the `UIErrorService` class.
 * The component also accesses the current user data from the `dataStore` for
 * error logging purposes.
 * @param {object} props - Component props.
 * @param {string} [props.testId] - Optional test identifier for the component.
 * @returns {ReactNode} The rendered error page component.
 */
const ErrorPage = ({ testId }: { testId?: string }): ReactNode => {
  const handleNavigate = () => {
    window.location.href = '/';
  };

  const { t } = useTranslation(['errors', 'common']);
  const error: any = useRouteError();

  const { dataStore } = useStore();

  useEffect(() => {
    if (error) {
      new UIErrorService({
        name: ERROR_TYPES.CLIENT,
        message: error?.message || ERROR_MESSAGES.DEFAULT,
        data: {
          user: JSON.parse(JSON.stringify(dataStore?.userStore?.currentUser)),
          error,
        },
      });
    }
  }, [error, dataStore?.userStore?.currentUser, t]);

  return (
    <div className="error-page" data-testid={testId}>
      <Result
        status="error"
        title={t('errors:something_went_wrong')}
        subTitle={t('errors:please_try_again_later')}
        extra={[
          <Button key="home-btn" type="primary" onClick={handleNavigate}>
            {t('common:back_home')}
          </Button>,
        ]}
      ></Result>
    </div>
  );
};
export default ErrorPage;
